import React, { useEffect, useState } from 'react';
import { Avatar } from '@boringer-avatars/react';
import DropdownMenu from '../DropdownMenu';
import { Modal } from './Modal';
import { ModalHeader } from './ModalHeader';
import { useClassInsights } from './ClassInsightsContext';
import {
  selectedClasses as getSelectedClasses,
  catalogClasses,
  studentsAcrossClasses,
} from './catalogSelectors';
import blackStudentIcon from '../../static/icons/Black-student-icon.svg';
import classChangeIcon from '../../static/icons/class-change-icon.svg';
import { ClassChangeRequestDialog } from '../Shared/ClassChangeRequestDialog';

const PIXEL_AVATAR_PALETTES = [
  ['#39FF14', '#FF1493', '#FFEB3B', '#FFFFFF', '#E0F7FA'],
  ['#0D47A1', '#5D4037', '#B39DDB', '#B3E5FC', '#FFFFFF'],
  ['#D84315', '#4DD0E1', '#FFFFFF', '#FFCC80', '#E0F7FA'],
  ['#E1BEE7', '#9E9D24', '#FFFFFF', '#C5E1A5', '#FFFDE7'],
  ['#1565C0', '#76FF03', '#FFF8E1', '#FFFFFF', '#29B6F6'],
  ['#A5D6A7', '#CE93D8', '#B0BEC5', '#FFFFFF', '#F5F5F5'],
  ['#311B92', '#1565C0', '#1976D2', '#FFFFFF', '#90CAF9'],
];

function paletteIndexForStudent(id, fallbackIndex) {
  const key = id != null ? String(id) : String(fallbackIndex);
  let hash = 0;
  for (let i = 0; i < key.length; i += 1) {
    hash = key.charCodeAt(i) + ((hash << 5) - hash);
  }
  return Math.abs(hash) % PIXEL_AVATAR_PALETTES.length;
}

const classLabelById = (classes, classId) => {
  const currentClass = classes.find((c) => String(c.id) === String(classId));
  return currentClass?.title ?? currentClass?.name ?? '';
};

const ALL_CLASSES = '__all_classes__';

export function StudentsPopup() {
  const { filters, catalog } = useClassInsights();
  // JED-1066: an empty class selection means "All classes". getSelectedClasses
  // deliberately returns [] for that, and the Header widens it locally — this
  // popup never did, so picking "All classes" listed nobody while the header
  // chip counted everyone.
  const classes = filters.classIds && filters.classIds.length > 0
    ? getSelectedClasses(catalog, filters)
    : catalogClasses(catalog);
  const [open, setOpen] = useState(false);
  const [classChangeRequestOpen, setClassChangeRequestOpen] = useState(false);
  // The in-popup pick is stored WITH the class scope it was made in. Validating
  // membership alone was not enough: an id that left the scope was only masked,
  // so re-selecting that class in the header resurrected it and pinned the list
  // to one class instead of the intended aggregate. Tying it to the scope makes
  // a pick valid only inside the scope it was chosen in.
  const [selection, setSelection] = useState({ scopeKey: '', id: null });

  const selectedClassLabels = filters.classIds
    .map((classId) => classLabelById(classes, classId))
    .filter(Boolean);

  // With more than one class in scope the list aggregates across all of them,
  // and the in-popup dropdown can still narrow to a single class.
  const canShowAll = classes.length > 1;
  // Canonicalised: the same set of classes must produce the same key regardless
  // of the order the catalog happens to return them in, or a refetch would
  // silently drop the teacher's pick.
  const scopeKey = classes.map((classItem) => String(classItem.id)).sort().join('|');
  const pickedId = selection.scopeKey === scopeKey ? selection.id : null;
  const effectiveClassId = pickedId
    || (canShowAll ? ALL_CLASSES : (classes[0] ? String(classes[0].id) : ''));
  const activeClass = effectiveClassId === ALL_CLASSES
    ? null
    : classes.find((classItem) => String(classItem.id) === effectiveClassId) || null;

  // Single class: pass the backend's ordering straight through. It already sorts
  // current-then-past by name, and re-sorting here with localeCompare would
  // silently reshuffle the common case (Java's compareTo is code-point order, so
  // the two disagree on case). Only the aggregated view needs a client-side sort,
  // because it is merging several already-sorted lists.
  const students = activeClass
    ? (activeClass.students || []).map((student) => ({
        ...student,
        className: activeClass.title || activeClass.name || '',
      }))
    : studentsAcrossClasses(classes);
  // Students who have work in this class but have since left it are returned
  // flagged rather than hidden: their submissions still count in every total,
  // so listing them greyed underneath is what makes those totals add up.
  const currentStudents = students.filter((s) => !s.isFormer);
  const pastStudents = students.filter((s) => s.isFormer);

  const currentSelectedId = activeClass ? String(activeClass.id) : (canShowAll ? ALL_CLASSES : '');
  const classOptions = canShowAll
    ? [{ id: ALL_CLASSES, title: 'All classes' }, ...classes]
    : classes;

  const parseSparkline = (value) =>
    String(value || '')
      .split(',')
      .map(Number)
      .filter((n) => Number.isFinite(n));

  const buildSparklinePoints = (values, width = 64, height = 20) => {
    if (!values.length) return '';
    const min = Math.min(...values);
    const max = Math.max(...values);
    const range = max - min || 1;
    return values
      .map((value, index) => {
        const x = values.length === 1 ? width / 2 : (index / (values.length - 1)) * width;
        const y = height - ((value - min) / range) * (height - 4) - 2;
        return `${x.toFixed(2)},${y.toFixed(2)}`;
      })
      .join(' ');
  };

  const trendClass = (label) => {
    const normalized = String(label || '').toLowerCase();
    if (normalized === 'steady') return 'steady';
    if (normalized === 'inconsistent') return 'inconsistent';
    if (normalized === 'improving') return 'improving';
    return '';
  };

  const handleClose = () => {
    setOpen(false);
    setClassChangeRequestOpen(false);
    setSelection({ scopeKey: '', id: null });
  };

  useEffect(() => {
    const handleOpenStudents = () => {
      // Discard any previous pick on open. handleClose already clears it and the
      // full-screen overlay means the header scope cannot change while the popup
      // is up, so this is belt-and-braces — it makes "a pick never outlives its
      // scope" true by construction rather than by relying on modal behaviour.
      setSelection({ scopeKey: '', id: null });
      setOpen(true);
    };
    window.addEventListener('openStudentsPopup', handleOpenStudents);
    return () => window.removeEventListener('openStudentsPopup', handleOpenStudents);
  }, []);

  if (!open) return null;

  return (
    <Modal
      onOverlayClick={handleClose}
      className="ci-students-modal"
    >
      <ModalHeader title="Students" onClose={handleClose}>
        {selectedClassLabels.length === 1 ? (
          <button
            type="button"
            className="ci-students-request-btn"
            onClick={() => setClassChangeRequestOpen(true)}
          >
            <img src={classChangeIcon} className="ci-students-request-btn-icon" alt="" />
            Request class change
          </button>
        ) : null}
      </ModalHeader>
      <ClassChangeRequestDialog
        open={classChangeRequestOpen}
        onClose={() => setClassChangeRequestOpen(false)}
      />

      <div className="ci-students-controls">
        <DropdownMenu
          multiselect
          singleSelect
          options={classOptions}
          selectedValues={currentSelectedId ? [currentSelectedId] : []}
          onChange={(values) => {
            if (values && values.length > 0) {
              setSelection({ scopeKey, id: values[0] });
            }
          }}
          placeholder="Select class"
          getOptionLabel={(opt) => opt.title || opt.name || 'Untitled Class'}
          getOptionValue={(opt) => String(opt.id)}
          alwaysWhite
          showAllOption={false}
          showCheckboxes={false}
          compactDisplay
          triggerWidth="auto"
          triggerMinWidth="300px"
          triggerFontSize="16px"
          triggerPadding="10px 16px"
        />
        <div className="ci-students-count-chip">
          <img src={blackStudentIcon} className="ci-students-count-icon" alt="" />
          {currentStudents.length} students
        </div>
        {pastStudents.length > 0 && (
          <div className="ci-students-count-chip ci-students-count-chip--past">
            +{pastStudents.length} past
          </div>
        )}
      </div>

      <div className="ci-students-table-head">
        <span>Student Name</span>
        <span>Performance</span>
      </div>

      <div className="ci-students-list">
        {renderStudentRows(currentStudents, 0)}
        {pastStudents.length > 0 && (
          <div className="ci-students-past-divider">
            Past students — no longer in this class, but their work is still counted below
          </div>
        )}
        {renderStudentRows(pastStudents, currentStudents.length)}
      </div>
    </Modal>
  );

  function renderStudentRows(rows, indexOffset) {
    return rows.map((student, i) => {
          const index = indexOffset + i;
          const paletteIndex = paletteIndexForStudent(student.id, index);
          const colors = PIXEL_AVATAR_PALETTES[paletteIndex];
          const sparkValues = parseSparkline(student?.performance?.sparkline);
          const points = buildSparklinePoints(sparkValues);
          const trendLabel = student?.performance?.trend?.label || '';
          const statusClass = trendClass(trendLabel);

          return (
            <div
              key={`${student.id}-${index}`}
              className={`ci-students-row${student.isFormer ? ' ci-students-row--past' : ''}`}
            >
              <div className="ci-students-row-left">
                <Avatar
                  variant="pixel"
                  colors={colors}
                  name={student.name}
                  size={24}
                />
                <div className="ci-students-names">
                  <div className="ci-students-name">{student.name}</div>
                  <div className="ci-students-sub">{student.className}</div>
                </div>
              </div>
              <div className="ci-students-row-right">
                {points ? (
                  <>
                    <svg
                      className={`ci-students-spark-svg ${statusClass}`.trim()}
                      viewBox="0 0 64 20"
                      fill="none"
                      aria-hidden="true"
                    >
                      <polyline points={points} fill="none" strokeWidth="1.8" />
                    </svg>
                    {!!trendLabel && (
                      <span className={`ci-students-trend ${statusClass}`.trim()}>
                        {trendLabel}
                      </span>
                    )}
                  </>
                ) : null}
                {!points && <div className="ci-students-no-data">No data</div>}
              </div>
            </div>
          );
    });
  }
}
